// SPDX-FileCopyrightText: © 2025 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface, EventEmitter } from '@stencil/core'
import {
  AttachInternals,
  Component,
  Event,
  h,
  Listen,
  Prop,
  readTask,
  State,
  Watch,
} from '@stencil/core'

import type { QdsFocusEventDetail } from '../../helpers'
import {
  invariant,
  isOverflowing,
  pickFocusEventAttributes,
} from '../../helpers'
import type { Importance, Size } from '../shared'

export type ActionItemImportance = Importance | 'destructive'

/**
 *
 *
 * @see https://quartz.se.com/build/components/action-item
 */
@Component({
  tag: 'qds-action-item',
  shadow: true,
  styleUrl: 'action-item.css',
})
export class ActionItem implements ComponentInterface {
  /**
   * The action item's importance.
   */
  @Prop() public readonly importance?: ActionItemImportance = 'standard'

  /**
   * Prevents the action item from being interacted with: it cannot be clicked
   * or focused.
   */
  @Prop() public readonly disabled: boolean = false

  /**
   * The name of the icon to render.
   *
   * Available names depend on the icon library being used.
   */
  @Prop() public readonly iconName?: string

  /**
   * The name of a registered icon library.
   */
  @Prop() public readonly iconLibrary: string = 'default'

  /**
   * The name of the action icon to render.
   *
   * Available names exist on the core icon library.
   */
  @Prop() public readonly action?: string

  /**
   * The action icon's accessible text that will be used by
   * screen readers.
   */
  @Prop() public readonly actionDescription?: string

  /**
   * The action items's size.
   */
  @Prop() public readonly size?: Size = 'standard'

  /**
   * The action item's text.
   */
  @Prop() public readonly text!: string

  @State() private tooltip = false

  /**
   * Emitted when the action item loses focus.
   */
  @Event({ eventName: 'qdsBlur', bubbles: false, cancelable: false })
  private readonly blurEmitter!: EventEmitter<QdsFocusEventDetail>

  /**
   * Emitted when the action item gains focus.
   */
  @Event({ eventName: 'qdsFocus', bubbles: false, cancelable: false })
  private readonly focusEmitter!: EventEmitter<QdsFocusEventDetail>

  @AttachInternals() private readonly internals!: ElementInternals

  #ro?: ResizeObserver

  #actionItem?: HTMLDivElement

  #span?: HTMLSpanElement

  get #computedSize(): Size {
    switch (this.size) {
      case 'standard':
      case 'small':
      case 'large': {
        return this.size
      }
      default: {
        return 'standard'
      }
    }
  }

  get #computedImportance(): ActionItemImportance {
    switch (this.importance) {
      case 'emphasized':
      case 'subdued':
      case 'destructive': {
        return this.importance
      }
      default: {
        return 'standard'
      }
    }
  }

  get #computedDisabled(): boolean {
    return this.disabled
  }

  @Watch('disabled')
  protected disabledChanged(): void {
    this.internals.ariaDisabled = this.disabled.toString()
  }

  @Listen('blur')
  protected onBlur(event: FocusEvent): void {
    this.blurEmitter.emit(pickFocusEventAttributes(event))
  }

  @Listen('focus')
  protected onFocus(event: FocusEvent): void {
    this.focusEmitter.emit(pickFocusEventAttributes(event))
  }

  @Listen('click')
  protected onClick(event: MouseEvent): void {
    if (this.#computedDisabled) event.stopImmediatePropagation()
  }

  public componentWillLoad(): void {
    this.internals.role = 'menuitem'
    this.disabledChanged()
  }

  public componentDidLoad(): void {
    invariant(this.#span)

    const span = this.#span
    readTask(() => {
      this.tooltip = isOverflowing(span)
    })
    this.#ro = new ResizeObserver(([spanEntry]) => {
      this.tooltip = isOverflowing(spanEntry.target)
    })
    this.#ro.observe(this.#span)
  }

  public render() {
    return (
      <div
        class={{
          'qds-action-item': true,
          'qds-disabled': this.disabled,
        }}
        ref={this.#actionDivRef}
        data-size={this.#computedSize}
        data-importance={this.#computedImportance}
      >
        {this.iconName !== undefined && this.iconName !== '' && (
          <qds-icon
            aria-hidden="true"
            class="qds-icon"
            data-size={this.#computedSize}
            library={this.iconLibrary}
            name={this.iconName}
          />
        )}
        <span
          class="qds-text"
          data-size={this.#computedSize}
          ref={this.#spanRef}
        >
          {this.text}
        </span>

        {this.action !== undefined && (
          <qds-icon
            class="qds-icon"
            data-size={this.#computedSize}
            name={this.action}
            library="core"
            aria-label={this.actionDescription}
            role="img"
          />
        )}
        {this.tooltip && (
          <qds-tooltip aria-hidden="true" ref={this.#tooltipRef}>
            {this.text}
          </qds-tooltip>
        )}
      </div>
    )
  }

  readonly #actionDivRef = (actionItem?: HTMLDivElement): void => {
    this.#actionItem = actionItem
  }

  readonly #spanRef = (span?: HTMLSpanElement): void => {
    this.#span = span
  }

  readonly #tooltipRef = (tooltip?: HTMLQdsTooltipElement): void => {
    if (tooltip)
      // eslint-disable-next-line no-param-reassign
      tooltip.target = this.#actionItem
  }
}
